import { useState } from 'react';
import {
  exportToCSV,
  exportToJSON,
  downloadFile,
  generateExportFilename,
  type TaskDependency,
} from '@/utils/exportUtils';
import { useToast } from '@/hooks/useToast';
import type { Database } from '@/types/database.types';
import type { Sprint } from '@/types/sprint';

type Project = Database['public']['Tables']['projects']['Row'];
type Task = Database['public']['Tables']['tasks']['Row'];
type TeamMember = Database['public']['Tables']['team_members']['Row'];
type Milestone = Database['public']['Tables']['milestones']['Row'];

interface ExportButtonProps {
  projects: Project[];
  tasks: Task[];
  teamMembers: TeamMember[];
  dependencies: TaskDependency[];
  milestones?: Milestone[];
  sprints?: Sprint[];
}

export const ExportButton = ({
  projects,
  tasks,
  teamMembers,
  dependencies,
  milestones,
  sprints,
}: ExportButtonProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { showToast } = useToast();

  const filenamePrefix = 'team-planner-export';

  const handleExport = async (format: 'csv' | 'json') => {
    setIsOpen(false);
    setIsExporting(true);
    try {
      let content: string;
      let mimeType: string;

      if (format === 'csv') {
        content = exportToCSV(projects, tasks, teamMembers, dependencies);
        mimeType = 'text/csv;charset=utf-8;';
      } else {
        content = exportToJSON(projects, tasks, teamMembers, dependencies, milestones, sprints);
        mimeType = 'application/json;charset=utf-8;';
      }

      const filename = generateExportFilename(filenamePrefix, format);
      downloadFile(content, filename, mimeType);

      showToast({
        title: 'Export complete',
        message: `${tasks.length} tasks exported as ${format.toUpperCase()}`,
        type: 'success',
      });
    } catch (err) {
      showToast({
        title: 'Export failed',
        message: err instanceof Error ? err.message : 'Unknown error',
        type: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(prev => !prev)}
        disabled={isExporting}
        className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors shadow-sm border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label="Export data"
      >
        {isExporting ? (
          <>
            <svg
              className="w-4 h-4 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            Exporting...
          </>
        ) : (
          <>
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Export
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {isOpen && !isExporting && (
        <>
          {/* Backdrop to close dropdown */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <div
            className="absolute right-0 mt-2 w-44 bg-white rounded-lg shadow-lg border border-gray-200 z-20"
            role="menu"
            aria-label="Export options"
          >
            <button
              onClick={() => handleExport('csv')}
              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-t-lg"
              role="menuitem"
            >
              <svg
                className="w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Export as CSV
            </button>
            <button
              onClick={() => handleExport('json')}
              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-b-lg"
              role="menuitem"
            >
              <svg
                className="w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                />
              </svg>
              Export as JSON
            </button>
          </div>
        </>
      )}
    </div>
  );
};
